"use client";

import { useState, useEffect } from "react";
import { useSession } from "next-auth/react";
import { useTranslation } from "react-i18next";
import { resendVerificationEmail } from "@/app/lib/unified-api";
import { createLogger } from "@/app/lib/logger";
import { useCurrentUser } from "@/app/hooks/swr/useCurrentUser";

const log = createLogger("EmailVerificationBanner");

const DISMISS_KEY = "emailVerificationBannerDismissed";
const DISMISS_DURATION_MS = 24 * 60 * 60 * 1000; // 24 hours

export default function EmailVerificationBanner() {
  const { data: session, status } = useSession();
  const { isEmailVerified } = useCurrentUser();
  const { t } = useTranslation();
  const [isDismissed, setIsDismissed] = useState(true); // Start dismissed to avoid flash
  const [isResending, setIsResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [resendError, setResendError] = useState(false);

  // Check if banner should be shown
  // Use isEmailVerified from useCurrentUser which checks both session AND backend data
  useEffect(() => {
    if (status !== "authenticated" || !session?.user) {
      setIsDismissed(true);
      return;
    }

    // If user is verified (from session OR backend), don't show banner
    if (isEmailVerified) {
      setIsDismissed(true);
      return;
    }

    // Check if user dismissed the banner recently
    const dismissedAt = localStorage.getItem(DISMISS_KEY);
    if (dismissedAt) {
      const dismissedTime = parseInt(dismissedAt, 10);
      if (Date.now() - dismissedTime < DISMISS_DURATION_MS) {
        setIsDismissed(true);
        return;
      }
    }

    // Show the banner
    setIsDismissed(false);
  }, [session, status, isEmailVerified]);

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, Date.now().toString());
    setIsDismissed(true);
  };

  const handleResend = async () => {
    if (!session?.user?.email) return;

    setIsResending(true);
    setResendSuccess(false);
    setResendError(false);

    try {
      await resendVerificationEmail(session.user.email);
      setResendSuccess(true);
      setTimeout(() => setResendSuccess(false), 5000);
    } catch (error) {
      log.error("Failed to resend verification email", error, {
        email: session?.user?.email,
      });
      setResendError(true);
      setTimeout(() => setResendError(false), 5000);
    } finally {
      setIsResending(false);
    }
  };

  // Don't render if dismissed or user is verified
  if (isDismissed || status !== "authenticated" || isEmailVerified) {
    return null;
  }

  return (
    <div className="bg-amber-50 border-b border-amber-200">
      <div className="max-w-7xl mx-auto px-4 py-3 sm:px-6 lg:px-8">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-amber-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
            </div>
            <p className="text-sm text-amber-800">
              <span className="font-medium">
                {t(
                  "emailVerificationBanner.title",
                  "Please verify your email address"
                )}
              </span>
              <span className="hidden sm:inline">
                {" - "}
                {t(
                  "emailVerificationBanner.message",
                  "Check your inbox for the verification link."
                )}
              </span>
            </p>
          </div>

          <div className="flex items-center gap-3">
            {resendSuccess ? (
              <span className="text-sm text-green-700 font-medium">
                {t("emailVerificationBanner.resendSuccess", "Email sent!")}
              </span>
            ) : resendError ? (
              <span className="text-sm text-red-700 font-medium">
                {t("emailVerificationBanner.resendError", "Failed to send")}
              </span>
            ) : (
              <button
                onClick={handleResend}
                disabled={isResending}
                className="text-sm font-medium text-amber-800 hover:text-amber-900 underline disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isResending
                  ? t("common.loading", "Sending...")
                  : t("emailVerificationBanner.resendButton", "Resend email")}
              </button>
            )}

            <button
              onClick={handleDismiss}
              className="flex-shrink-0 p-1 rounded-md hover:bg-amber-100 transition-colors"
              aria-label={t("common.dismiss", "Dismiss")}
            >
              <svg
                className="h-4 w-4 text-amber-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
